import { FC } from "react";
import { useTranslation } from "react-i18next";

import { MuiButton } from "@ryde/controls";
import { IAddressSearchBounds } from "@ryde/shared";

import { BoundsBlockEdges } from "./bounds-block-edges";
import { BoundsBlockMapStyled, BoundsBlockStyled } from "./bounds-block.styled";
import {
  getBoundsCenter,
  getBoundsHintKey,
  getBoundsZoom,
  isValidAddressSearchBounds,
} from "./bounds-block.utils";
import { BoundsCropLayer } from "./bounds-crop-layer";
import { useBoundsBlockEditor } from "./use-bounds-block-editor";

export interface IBoundsBlockProps {
  value?: IAddressSearchBounds;
  /** What Reset restores — the rectangle the field was seeded with. */
  defaultBounds?: IAddressSearchBounds;
  /** `null` clears the parameter, which is what "no area limit" is stored as. */
  onChange: (value: IAddressSearchBounds | null) => void;
}

/**
 * The rectangle address search is confined to: shown on a map, cropped by dragging a corner
 * handle or by typing the four edges.
 *
 * The area is always a rectangle, because that is the only shape Google's autocomplete takes.
 * Clearing it is a real setting rather than an empty form: it means address search is not
 * confined at all.
 */
export const BoundsBlock: FC<IBoundsBlockProps> = ({
  value,
  defaultBounds,
  onChange,
}) => {
  const { t } = useTranslation();
  const {
    draft,
    isEditing,
    isSaveable,
    canResetToDefault,
    startEditing,
    cancelEditing,
    save,
    resetToDefault,
    clear,
    changeEdge,
    moveCorner,
  } = useBoundsBlockEditor({ value, defaultBounds, onChange });

  return (
    <BoundsBlockStyled>
      <p className="description">
        {t("systemParam.addressSearchBounds.description")}
      </p>

      <div className="actions">
        {isEditing ? (
          <>
            <MuiButton onClick={save} disabled={!isSaveable}>
              {t("systemParam.addressSearchBounds.save")}
            </MuiButton>
            <MuiButton onClick={cancelEditing}>
              {t("systemParam.addressSearchBounds.cancel")}
            </MuiButton>
            {/* Absent while the draft already is the default: there would be nothing to
                reset to, and a button that changes nothing reads as a broken one. */}
            {canResetToDefault && (
              <MuiButton onClick={resetToDefault}>
                {t("systemParam.addressSearchBounds.reset")}
              </MuiButton>
            )}
          </>
        ) : (
          <>
            <MuiButton onClick={startEditing}>
              {t("systemParam.addressSearchBounds.edit")}
            </MuiButton>
            {/* Nothing to remove when no rectangle is saved, so the button is absent rather
                than dead — and a half-filled value is not a rectangle either. */}
            {isValidAddressSearchBounds(value) && (
              <MuiButton onClick={clear}>
                {t("systemParam.addressSearchBounds.clear")}
              </MuiButton>
            )}
          </>
        )}
        <span className="hint">
          {t(
            `systemParam.addressSearchBounds.${getBoundsHintKey({ bounds: draft, isEditing, isSaveable })}`,
          )}
        </span>
      </div>

      <BoundsBlockEdges
        bounds={draft}
        isDisabled={!isEditing}
        onEdgeChange={changeEdge}
      />

      {draft && (
        // Framed on the rectangle at first paint. The map is not remounted afterwards, so
        // panning and zooming stay where the operator left them while they crop.
        <BoundsBlockMapStyled
          initialCenter={getBoundsCenter(draft)}
          initialZoom={getBoundsZoom(draft)}
        >
          <BoundsCropLayer
            bounds={draft}
            isEditing={isEditing}
            onCornerMove={moveCorner}
          />
        </BoundsBlockMapStyled>
      )}
    </BoundsBlockStyled>
  );
};
